/**
 * CC_08 (v1.3.3) — Nachzug des CC_07-Abschneide-Blocks: die kosmetische Per-Typ-
 * Tiefe der Listen-Spalten (D-42, Mockup v3). Pure Formatierer, damit sie ohne
 * DOM unit-testbar sind; die Zellen in RyfeList wenden sie nur an.
 *
 *   number → Dezimal-/Tausender-Separator + Währungs-Symbol (aus config)
 *   url    → Link/Button-Umschaltung + Label (aus config)
 */

export interface NumberFormat {
  decimalSep?: string;
  thousandSep?: string;
  currency?: string;
}

/**
 * Format a numeric cell value per its column config. An empty value → "".
 * A value that is not a finite number is returned unchanged (never throws).
 */
export const formatNumber = (
  value: unknown,
  cfg: NumberFormat = {},
): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const n =
    typeof value === "number"
      ? value
      : Number(String(value).trim().replace(",", "."));
  if (!Number.isFinite(n)) {
    return String(value);
  }
  const dec = cfg.decimalSep || ".";
  const th = cfg.thousandSep || "";
  const [intPart, fracPart] = Math.abs(n).toString().split(".");
  const grouped = th ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, th) : intPart;
  const body = (n < 0 ? "-" : "") + grouped + (fracPart ? dec + fracPart : "");
  return cfg.currency ? `${cfg.currency} ${body}` : body;
};

export interface UrlFormat {
  display?: "link" | "button";
  label?: string;
}

/** Normalise a url column's display config (defaults: link, no label). */
export const urlView = (
  cfg: UrlFormat = {},
): { display: "link" | "button"; label: string } => ({
  display: cfg.display === "button" ? "button" : "link",
  label: typeof cfg.label === "string" ? cfg.label : "",
});

/** The text shown for a url cell: the configured label, else the URL itself. */
export const urlLabelFor = (value: unknown, cfg: UrlFormat = {}): string => {
  const label = urlView(cfg).label.trim();
  return label || (value ? String(value) : "");
};

/**
 * CC_09 Block 1 (P22) — die Render-Hälfte der neuen Spaltentypen `url_text` und
 * `color`. Der DB-type-CHECK + Backend-Allowlist, die sie persistierbar machen,
 * werden in CC_11 (Migration 019) konsolidiert; diese reinen Formatierer sind
 * bereits jetzt vorhanden und unit-getestet, damit RyfeList sie ohne DOM-Logik
 * anwenden kann, sobald die Typen freigeschaltet sind.
 */

/**
 * `url_text` — eine Zelle mit sichtbarem Text UND separatem Link-Ziel. Der
 * gespeicherte Wert ist entweder ein JSON-Objekt `{text,url}` oder ein blanker
 * String (dann Text = URL). Immer beide Felder als getrimmte Strings zurück,
 * nie werfend.
 */
export const urlTextCell = (value: unknown): { text: string; url: string } => {
  if (value && typeof value === "object") {
    const o = value as { text?: unknown; url?: unknown };
    return {
      text: typeof o.text === "string" ? o.text.trim() : "",
      url: typeof o.url === "string" ? o.url.trim() : "",
    };
  }
  const raw = value === null || value === undefined ? "" : String(value).trim();
  if (raw.startsWith("{")) {
    try {
      return urlTextCell(JSON.parse(raw));
    } catch {
      /* kein JSON → als blanke URL behandeln */
    }
  }
  return { text: "", url: raw };
};

/** Der anzuzeigende Text einer url_text-Zelle: der Text, sonst die URL. */
export const urlTextLabel = (value: unknown): string => {
  const { text, url } = urlTextCell(value);
  return text || url;
};

/**
 * `color` — normalisiert einen Farbwert auf `#rrggbb` (Kleinbuchstaben).
 * Akzeptiert `#rgb`/`#rrggbb` mit oder ohne führendes `#`. Ungültige oder leere
 * Werte → "" (die Zelle zeigt dann keinen Farbtupfer).
 */
export const normalizeColor = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  let s = String(value).trim().toLowerCase();
  if (s.startsWith("#")) {
    s = s.slice(1);
  }
  if (/^[0-9a-f]{3}$/.test(s)) {
    s = s
      .split("")
      .map((c) => c + c)
      .join("");
  }
  return /^[0-9a-f]{6}$/.test(s) ? `#${s}` : "";
};
